import { motion } from "framer-motion";
import { LineChart, ShieldCheck, TicketCheck, Gauge } from "lucide-react";
import Watermark from "@/components/landing/Watermark";

const cards = [
  {
    title: "Transparencia Económica y Conciliación Continua",
    description: "Conciliación bancaria periódica y acceso permanente a facturas, movimientos y balances a través del área digital. Cuentas claras, auditadas y disponibles sin esperas ni opacidad.",
    icon: LineChart,
    testId: "strength-card-cuentas",
  },
  {
    title: "Mediación Estratégica y Prevención de la Deuda",
    description: "Protocolos de acuerdo extrajudicial mediante ofertas vinculantes y requerimientos formales fehacientes. Protegemos la tesorería comunitaria y la convivencia agotando la buena fe antes de la vía judicial.",
    icon: ShieldCheck,
    testId: "strength-card-morosidad",
  },
  {
    title: "Gestión Técnica Operativa y Canal Oficial de Incidencias",
    description: "Protocolo estructurado que distingue urgencias críticas de mantenimientos ordinarios. Registro digital de cada aviso para que ninguna gestión dependa de llamadas perdidas ni sobrecargue al presidente.",
    icon: TicketCheck,
    testId: "strength-card-averias",
  },
  {
    title: "Auditoría de Proveedores y Eficiencia en Costes",
    description: "Revisión exhaustiva de contratos de mantenimiento (ascensores, bombeos, seguros y suministros). Exigencia de cumplimiento estricto a las contratas para maximizar el rendimiento de cada euro comunitario.",
    icon: Gauge,
    testId: "strength-card-ahorro",
  },
];

export default function Sello() {
  return (
    <section id="el-sello" data-testid="sello-section" className="relative pt-6 pb-12 sm:pt-6 sm:pb-8 lg:pt-6 lg:pb-12">
      <Watermark />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="grid lg:grid-cols-2 gap-12 sm:gap-8 lg:gap-16 items-center mb-6 sm:mb-5 lg:mb-6"
        >
          <div className="text-left max-w-2xl mx-auto lg:mx-0">
          <h2 className="font-display section-title text-slate-900" data-testid="sello-title">
            El Sello AFGestiones: Gestión con Firma Propia
          </h2>
          <p className="section-subtitle" data-testid="sello-subtitle">
            Elegancia Administrativa, Presencia Técnica y Acuerdos con Solvencia.
          </p>
          <div className="mt-6 space-y-4 text-base text-slate-500 leading-relaxed" data-testid="sello-intro">
            <p>
              Entendemos la administración de fincas lejos de la improvisación y de los modelos masificados de atención impersonal. Aplicamos una metodología orientada al orden contable, el cuidado directo del inmueble y el trato institucional.
            </p>
            <p>
              Priorizamos el diálogo fundamentado y los acuerdos de buena fe para disolver discrepancias y blindar los acuerdos de junta, con respaldo técnico y legal especializado. Una gestión previsible que protege el patrimonio y aporta tranquilidad al día a día de los propietarios.
            </p>
          </div>
          </div>
          <div className="overflow-hidden rounded-[14px] mt-6 lg:mt-0" data-testid="sello-image">
            <img
              src="/images/sello-arquitectura-editorial.jpg"
              alt="Acceso comunitario impecable en edificio residencial contemporáneo con materiales nobles"
              className="w-full h-[240px] max-h-[260px] sm:h-[240px] lg:h-full lg:max-h-none lg:min-h-[360px] object-cover"
              loading="lazy"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8" data-testid="sello-grid">
          {cards.map(({ title, description, icon: Icon, testId }, i) => (
            <motion.article
              key={testId}
              data-testid={testId}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: "easeOut" }}
              className="group border border-slate-200 hover:border-slate-900 rounded-xl p-5 transition-colors duration-500 bg-white"
            >
              <Icon className="w-6 h-6 text-slate-400 group-hover:text-slate-900 group-hover:-translate-y-1 transition-all duration-500 mb-6" />
              <h3 className="font-display card-title text-slate-900 leading-snug">{title}</h3>
              <p className="mt-4 text-sm text-slate-500 leading-relaxed">{description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
